import {
    Badge,
    Box,
    ExpandableSection,
    Select,
    SelectProps,
    Spinner,
} from '@cloudscape-design/components';
import { AxiosError } from 'axios';
import dayjs from 'dayjs';
import DOMPurify from 'dompurify';
import parse, {
    DOMNode,
    Element,
    HTMLReactParserOptions,
    Text,
    domToReact,
} from 'html-react-parser';
import { useEffect, useRef, useState } from 'react';
import { useTranslate } from 'src/i18n/useTranslate';
import {
    TopicChangeHistoryEntry,
    TopicChanges,
    TopicDiff,
    TopicDiffSection,
} from './types';
import { useTopicDiff } from './useTopicChanges';
import {
    buildCacheKey,
    useWhatsChangedPanelCache,
} from './WhatsChangedPanelContext';
import './WhatsChangedPanel.scss';

type WhatsChangedPanelProps = {
    documentId: string;
    lcmsXmlGUID: string;
    /**
     * Parent-provided topic-changes metadata, hoisted to avoid a duplicate
     * fetch against the one the Badge already does. Passing `undefined`
     * (loading) hides the panel entirely. When `isNotTracked` is true the
     * panel still renders an empty-state message so users can tell apart
     * "topic genuinely has no recorded changes" from "something broke".
     */
    topicChanges: TopicChanges | undefined;
    /**
     * True when the topic has no recorded diff history — either because
     * the legacy service shape returned HTTP 404 or because the new
     * shape returned HTTP 200 with `latestChangeAt == null` (see
     * `useTopicChanges` for the OR semantics). The panel renders an
     * empty-state header in this branch; loading / network-error
     * branches still return null.
     */
    isNotTracked?: boolean;
};

/**
 * What's-Changed panel for the topic page (Phase 1e FE-03).
 *
 * - Hidden entirely when `topicChanges` is undefined (parent handles 404 /
 *   outside-window / loading).
 * - Cloudscape ExpandableSection, collapsed by default. Header: "What's
 *   changed". Secondary summary: "Last modified {date}, {N} section{s}".
 * - First expansion triggers a GET to the pre-rendered diff URL. The result
 *   is cached in WhatsChangedPanelContext so subsequent collapse/expand on
 *   the same mount does NOT refetch (Requirement 3.6).
 * - When `topicChanges.history.length > 1` a version Select is rendered
 *   above the sections, letting the viewer flip between the recorded
 *   history entries (newest-first). Each selection is fetched on demand
 *   and cached per `toVersionId`.
 * - On non-2xx / parse failure the body is hidden and one console.error is
 *   logged with the identifying triple. No automatic retry.
 * - `<ins>` and `<del>` (DOMPurify default-allowlist) plus `<iframe>` and
 *   `data-src` (needed because Xyleme topics legitimately embed iframes
 *   via a lazy-load pattern that reads `data-src`) all pass through
 *   `sanitizeDiffHtml`. The configured allowlist deliberately mirrors
 *   `useRenderContent` so the diff view and the base topic view sanitize
 *   identically — any topic HTML the base renderer accepts, the diff
 *   view also accepts; any script / event handler / `javascript:` URL
 *   both strip. The sanitized HTML is then handed to
 *   `html-react-parser`'s `parse` to produce React elements rather
 *   than `dangerouslySetInnerHTML`.
 */
export const WhatsChangedPanel = ({
    documentId,
    lcmsXmlGUID,
    topicChanges,
    isNotTracked = false,
}: WhatsChangedPanelProps) => {
    const t = useTranslate();
    const { getCached, setCached } = useWhatsChangedPanelCache();
    const diffApi = useTopicDiff();
    const [expanded, setExpanded] = useState(false);
    const [cachedDiff, setCachedDiff] = useState<TopicDiff | undefined>();
    const [hasError, setHasError] = useState(false);
    const [selectedIdx, setSelectedIdx] = useState(0);

    // The latest key loadDiffForVersion was fired for. Written at the
    // top of each call and read inside the awaited success / catch
    // branches to decide whether the response is still relevant.
    //
    // We cannot compare against the render-scoped `cacheKey` because
    // onSelectVersion fires `setSelectedIdx(idx)` and then immediately
    // `await loadDiffForVersion(nextVersionId)` in the same handler
    // tick, before React has re-rendered. `cacheKey` is derived from
    // the stale `selectedIdx`, so `key === cacheKey` would be FALSE
    // for every version switch — silently dropping both the success
    // (setCachedDiff never fires) and error (hasError never flips)
    // branches.
    const latestKeyRef = useRef<string | undefined>(undefined);

    // Reset per-panel UI state whenever the owning topic changes. Without
    // this, navigating between topics (same mount, different
    // documentId/lcmsXmlGUID) carries over the previous topic's expanded
    // state and selected-history index, which would then point at the
    // wrong entry in the new topic's `history` array.
    useEffect(() => {
        setExpanded(false);
        setSelectedIdx(0);
        setHasError(false);
        setCachedDiff(undefined);
        latestKeyRef.current = undefined;
    }, [documentId, lcmsXmlGUID]);

    const history = topicChanges?.history ?? [];
    const selectedEntry: TopicChangeHistoryEntry | undefined =
        history[selectedIdx];
    const selectedToVersionId = selectedEntry?.toVersionId;
    const cacheKey = selectedToVersionId
        ? buildCacheKey(documentId, lcmsXmlGUID, selectedToVersionId)
        : undefined;

    // When the selected version changes, promote a context-cache hit into
    // local state (so re-selecting a previously fetched version renders
    // instantly) or clear local state so a fresh fetch can take over.
    // We do NOT mirror `diffApi.data` into local state via a useEffect here
    // because that effect would race with version switches: when `cacheKey`
    // advances before the next fetch resolves, `diffApi.data` still holds
    // the previous version's payload, and caching it under the new key
    // would overwrite the context cache with stale content. Instead,
    // `loadDiffForVersion` captures the response from `makeRequest` directly
    // and caches it against the key the fetch was fired for.
    useEffect(() => {
        if (!cacheKey) return;
        const hit = getCached(cacheKey);
        if (hit) {
            setCachedDiff(hit);
            setHasError(false);
        } else {
            setCachedDiff(undefined);
        }
    }, [cacheKey, getCached]);

    if (!topicChanges || !selectedEntry || !selectedToVersionId || !cacheKey) {
        if (isNotTracked) {
            return (
                <div className="whats-changed-panel">
                    <ExpandableSection
                        variant="container"
                        headerText={t('whats-changed-header', "What's changed")}
                        headerDescription={t(
                            'whats-changed-empty-state',
                            'No recent changes recorded for this topic.'
                        )}
                        data-testid="whats-changed-panel-empty"
                    />
                </div>
            );
        }
        return null;
    }

    const n = selectedEntry.changedSections.length;
    const sectionWord =
        n === 1
            ? t('whats-changed-section-single', 'section')
            : t('whats-changed-sections-plural', 'sections');
    const summary = `${t(
        'whats-changed-summary-prefix',
        'Last modified'
    )} ${dayjs(selectedEntry.at).format(
        'MMM D, YYYY'
    )}, ${n} ${sectionWord} ${t('whats-changed-summary-suffix', 'changed')}`;

    const loadDiffForVersion = async (toVersionId: string) => {
        const key = buildCacheKey(documentId, lcmsXmlGUID, toVersionId);
        // Record this as the latest in-flight request before any state
        // updates or awaits. The success / catch branches compare
        // against `latestKeyRef.current` to decide whether a response
        // is still relevant — a later version switch overwrites this
        // ref with its own key, causing earlier awaits to bail out.
        // Comparing against the render-scoped `cacheKey` instead would
        // fail: onSelectVersion fires setSelectedIdx(idx) and then
        // immediately await loadDiffForVersion(nextVersionId) in the
        // same tick, so cacheKey (derived from the stale selectedIdx)
        // never matches `key` on a version switch.
        latestKeyRef.current = key;
        const hit = getCached(key);
        if (hit) {
            setCachedDiff(hit);
            setHasError(false);
            return;
        }
        setHasError(false);
        try {
            // Capture the response directly off makeRequest so we can
            // cache it against the key the fetch was fired for — avoids
            // a race where a later version switch would otherwise
            // cache stale `diffApi.data` under the new cacheKey.
            //
            // `useTopicDiff` passes `throwOnError: true` so a failure
            // rejects this promise rather than silently landing in the
            // shared `diffApi.error` state. Attributing the error to
            // THIS specific request (via the catch block below) avoids
            // the cross-topic error leak that an effect watching
            // `diffApi.error` would introduce — on doc/guid change,
            // the shared error would still be non-null from the prior
            // topic's failed fetch and would wrongly fire for the new
            // topic even before we called makeRequest.
            const response = await diffApi.makeRequest({
                url: `topic-changes/${documentId}/${lcmsXmlGUID}/diff/${toVersionId}`,
            });
            const payload = response?.data as TopicDiff | undefined;
            if (payload) {
                setCached(key, payload);
                // Only promote into local state if the user is still
                // on this version. Otherwise the context-cache
                // useEffect will promote the hit on the next render
                // once the new version's cacheKey settles.
                if (key === latestKeyRef.current) {
                    setCachedDiff(payload);
                }
            }
        } catch (err) {
            // Only react to the error if the user is still on the
            // version this fetch was fired for. A later version switch
            // would have already overwritten latestKeyRef with its own
            // key; letting this older rejection flip hasError would
            // surface a stale error under the new version.
            if (key !== latestKeyRef.current) {
                return;
            }
            setHasError(true);
            console.error('WhatsChangedPanel diff fetch failed', {
                documentId,
                lcmsXmlGUID,
                toVersionId,
                kind:
                    err instanceof AxiosError && err.response?.status === 404
                        ? 'not-found'
                        : 'exception',
            });
        }
    };

    const onExpandChange = async ({
        detail,
    }: {
        detail: { expanded: boolean };
    }) => {
        setExpanded(detail.expanded);
        if (!detail.expanded) return;
        if (cachedDiff) return;
        await loadDiffForVersion(selectedToVersionId);
    };

    const historyOptions: SelectProps.Option[] = history.map((entry, idx) => ({
        value: String(idx),
        label:
            idx === 0
                ? `${t('whats-changed-history-latest', 'Latest')} — ${dayjs(
                      entry.at
                  ).format('MMM D, YYYY')}`
                : dayjs(entry.at).format('MMM D, YYYY'),
        description:
            entry.changedSections.length === 1
                ? `1 ${t('whats-changed-section-single', 'section')}`
                : `${entry.changedSections.length} ${t(
                      'whats-changed-sections-plural',
                      'sections'
                  )}`,
    }));
    const selectedOption =
        historyOptions.find((o) => o.value === String(selectedIdx)) ?? null;

    const onSelectVersion = async (option: SelectProps.Option | null) => {
        if (!option || option.value === undefined) return;
        const idx = Number(option.value);
        if (Number.isNaN(idx) || idx === selectedIdx) return;
        setSelectedIdx(idx);
        if (!expanded) return;
        const nextVersionId = history[idx]?.toVersionId;
        if (!nextVersionId) return;
        await loadDiffForVersion(nextVersionId);
    };

    return (
        <div className="whats-changed-panel">
            <ExpandableSection
                variant="container"
                headerText={t('whats-changed-header', "What's changed")}
                headerDescription={summary}
                expanded={expanded}
                onChange={onExpandChange}
                data-testid="whats-changed-panel"
            >
                {expanded && history.length > 1 && (
                    <div
                        className="whats-changed-version-picker"
                        data-testid="whats-changed-version-picker"
                    >
                        <Box variant="awsui-key-label">
                            {t('whats-changed-version-picker-label', 'Version')}
                        </Box>
                        <Select
                            selectedOption={selectedOption}
                            onChange={({ detail }) =>
                                void onSelectVersion(detail.selectedOption)
                            }
                            options={historyOptions}
                            data-testid="whats-changed-version-select"
                        />
                    </div>
                )}
                {renderBody({
                    expanded,
                    loading: diffApi.loading,
                    hasError,
                    diff: cachedDiff,
                    t,
                })}
            </ExpandableSection>
        </div>
    );
};

// SECURITY: The ADD_TAGS/ADD_ATTR widening deliberately matches the one
// in src/components/ArtifactDetail/components/useRenderContent.tsx. Both
// renderers consume the same Xyleme-authored topic HTML (the base view
// renders the latest version, the diff view renders ins/del-annotated
// segments of it) and must sanitize identically — if useRenderContent
// strips something the diff keeps, the two views disagree on what a
// topic contains; if the diff strips something useRenderContent keeps,
// legitimate embedded content disappears from the "What's changed"
// panel. `<iframe>` + `data-src` are present in Xyleme content via a
// lazy-load pattern and both renderers need them. DOMPurify still
// strips `<script>`, event-handler attributes, and `javascript:` URLs
// in both renderers.
const sanitizeDiffHtml = (html: string): string =>
    DOMPurify.sanitize(html, {
        ADD_TAGS: ['iframe'],
        ADD_ATTR: ['data-src'],
    });

/**
 * True if `node` and every descendant is either an `<img>` element, an
 * element whose own subtree is image-only (so legacy
 * `<div class="Icon"><img/></div>` wrappers still qualify), whitespace-
 * only text, or an inconsequential node type (comments, etc.). Any text
 * node with visible content or any non-image element carrying text makes
 * the subtree mixed.
 *
 * Pairs with `hasAnyImg` to guard against an empty wrapper (which would
 * otherwise match the vacuous "every child is image-only" case) falsely
 * triggering the image-badge transform.
 */
const isImageOnly = (node: DOMNode): boolean => {
    if (node instanceof Element) {
        if (node.name === 'img') return true;
        const children = (node.children as DOMNode[] | undefined) ?? [];
        return children.every(isImageOnly);
    }
    if (node instanceof Text) {
        return node.data.trim().length === 0;
    }
    // Comments / processing instructions / other node types — neutral.
    return true;
};

/**
 * True if the subtree contains at least one `<img>` element. HtmlInlineDiffer
 * emits two shapes for image diffs:
 *   - current (post-5a4a0d7): <del><img>...</del>
 *   - legacy (still in the beta diffs bucket): <del><div class="Icon"><img></div></del>
 * so a direct-child check wouldn't catch the legacy shape.
 */
const hasAnyImg = (node: DOMNode): boolean => {
    if (!(node instanceof Element)) return false;
    if (node.name === 'img') return true;
    return (node.children as DOMNode[] | undefined)?.some(hasAnyImg) ?? false;
};

const renderBody = ({
    expanded,
    loading,
    hasError,
    diff,
    t,
}: {
    expanded: boolean;
    loading: boolean;
    hasError: boolean;
    diff: TopicDiff | undefined;
    t: (key: string, defaultValue: string) => string;
}) => {
    if (!expanded) return null;
    if (hasError) return null;
    if (loading && !diff) {
        return <Spinner data-testid="whats-changed-spinner" />;
    }
    if (!diff) return null;

    return (
        <div data-testid="whats-changed-sections">
            {diff.sections.map((section, idx) => (
                <SectionRow key={idx} section={section} t={t} />
            ))}
        </div>
    );
};

const SectionRow = ({
    section,
    t,
}: {
    section: TopicDiffSection;
    t: (key: string, defaultValue: string) => string;
}) => {
    if (section.unchanged) {
        return (
            <div
                className="whats-changed-section whats-changed-section-unchanged"
                data-testid="whats-changed-section-unchanged"
            >
                <Box variant="h4">{section.sectionTitle}</Box>
            </div>
        );
    }
    // html-react-parser replace hook. For a <del>/<ins> wrapper whose
    // every meaningful child is image-only (an <img>, an image-only
    // subtree such as the legacy nested `<div class="Icon"><img/></div>`
    // shape, or whitespace), swap the wrapper for a labeled <span>
    // carrying an "Image removed" / "Image added" badge. Mixed wrappers
    // that also carry visible text (e.g. `<del><img/>caption</del>`)
    // fall through to native <del>/<ins> rendering so the text keeps
    // the strikethrough + tint from the scoped text-level SCSS rules;
    // the tradeoff is the browser's strikethrough drawing through the
    // image center in those (rare) cases.
    //
    // The browserslist in this package targets browsers that pre-date
    // `:has()` (Chrome/Firefox 45, Safari 10.1), so a pure-CSS
    // `del:has(img)` selector would silently no-op on supported
    // targets — doing the transform in TSX makes the badge treatment
    // work uniformly.
    const diffParseOptions: HTMLReactParserOptions = {
        replace: (domNode) => {
            if (!(domNode instanceof Element)) return;
            if (domNode.name !== 'del' && domNode.name !== 'ins') return;
            const children = (domNode.children as DOMNode[] | undefined) ?? [];
            if (
                !(
                    children.length > 0 &&
                    children.every(isImageOnly) &&
                    children.some(hasAnyImg)
                )
            )
                return;
            const kind = domNode.name === 'del' ? 'removed' : 'added';
            const wrapperClass = `whats-changed-image-${kind}`;
            const badgeLabel =
                kind === 'removed'
                    ? t('whats-changed-image-removed-badge', 'Image removed')
                    : t('whats-changed-image-added-badge', 'Image added');
            return (
                <span
                    className={wrapperClass}
                    data-testid={`whats-changed-image-${kind}-wrapper`}
                >
                    <span
                        className="whats-changed-image-badge"
                        data-testid={`whats-changed-image-${kind}-badge`}
                    >
                        {badgeLabel}
                    </span>
                    <span
                        className="whats-changed-image-figure"
                        data-testid={`whats-changed-image-${kind}-figure`}
                    >
                        {domToReact(children, diffParseOptions)}
                    </span>
                </span>
            );
        },
    };

    return (
        <div
            className="whats-changed-section"
            data-testid="whats-changed-section-changed"
        >
            <Box variant="h4">
                {section.sectionTitle}
                {section.hasImageChange && (
                    <>
                        {' '}
                        <Badge
                            color="blue"
                            data-testid="whats-changed-image-badge"
                        >
                            {t('whats-changed-image-badge', 'Image updated')}
                        </Badge>
                    </>
                )}
            </Box>
            {section.diffHtml && (
                // SECURITY: diff HTML renders through DOMPurify
                // (sanitizeDiffHtml) and then html-react-parser's
                // `parse`, which converts the sanitized HTML string
                // into real React elements rather than setting
                // innerHTML directly. Matches the base topic
                // renderer in useRenderContent, both to stay in
                // lockstep on what Xyleme content is accepted and to
                // avoid dangerouslySetInnerHTML — see the longer
                // security note at the sanitizeDiffHtml definition.
                <div className="whats-changed-section-body">
                    {parse(
                        sanitizeDiffHtml(section.diffHtml),
                        diffParseOptions
                    )}
                </div>
            )}
        </div>
    );
};
